import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export type AppRole =
  | "admin"
  | "moderator"
  | "user"
  | "super_admin"
  | "finance_admin"
  | "support_admin"
  | "merchant_admin";

// Roles that count as "any admin" for navigation purposes
export const ADMIN_ROLES: AppRole[] = [
  "admin",
  "super_admin",
  "finance_admin",
  "support_admin",
  "merchant_admin",
];

export const useUserRole = () => {
  const { user, loading: authLoading } = useAuth();
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user) {
        setRoles([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      if (!cancelled) {
        setRoles((data ?? []).map((r: { role: AppRole }) => r.role));
        setLoading(false);
      }
    };
    if (!authLoading) load();
    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  const isAdmin = roles.includes("admin") || roles.includes("super_admin");
  const isModerator = roles.includes("moderator");
  const isAnyAdmin = roles.some((r) => ADMIN_ROLES.includes(r));

  const hasAny = (allowed: AppRole[]) => allowed.some((r) => roles.includes(r));

  return { roles, isAdmin, isModerator, isAnyAdmin, hasAny, loading: loading || authLoading };
};
